// Helpers for the search page's Label menu (searchLabelsBlk.jsx / Result-filter.jsx).

// The API edge rejects request bodies over 8 KB (about 400 parcel ids), and a
// bulk selection can hold up to 10,000, so labels are applied in batches.
export const LABEL_ASSIGN_BATCH_SIZE = 150;

// Same rule as the backend: trimmed, inner whitespace collapsed.
export function normalizeLabelName(name) {
  return String(name ?? "").trim().replace(/\s+/g, " ");
}

// The label whose name matches ignoring case and spacing, other than exceptId
// (the label being renamed).
export function findLabelNamed(labels, name, exceptId = null) {
  const wanted = normalizeLabelName(name).toLowerCase();

  return (labels || []).find(
    (label) =>
      label?.id !== exceptId &&
      normalizeLabelName(label?.label_name).toLowerCase() === wanted
  );
}

// Adds each property's new labels to target (a { cardId: [label] } map),
// skipping labels it already holds.
export function mergePropertyLabels(target, incoming) {
  Object.entries(incoming || {}).forEach(([propertyId, labels]) => {
    const existing = target[propertyId] || [];
    const seen = new Set(existing.map((label) => label?.label_id));

    target[propertyId] = [
      ...existing,
      ...(labels || []).filter((label) => !seen.has(label?.label_id)),
    ];
  });

  return target;
}

export async function addLabelToPropertiesInBatches(
  client,
  labelId,
  propertyIds,
  batchSize = LABEL_ASSIGN_BATCH_SIZE
) {
  const result = {
    labelled: 0,
    alreadyLabelled: 0,
    skipped: 0,
    propertyLabels: {},
    error: null,
  };

  for (let i = 0; i < propertyIds.length; i += batchSize) {
    const batch = propertyIds.slice(i, i + batchSize);
    let data;

    try {
      ({ data } = await client.post("add-label-selected-properties", {
        label_id: labelId,
        selected_properties: batch,
      }));
    } catch (error) {
      data = null;
    }

    // A batch the server processed carries counts, even when it labelled
    // nothing. Anything else (refused, failed, unreachable) stops the run.
    if (typeof data?.labelled !== "number") {
      result.error = data?.msg || "Couldn't add the label. Please try again.";
      result.skipped += propertyIds.length - i;
      break;
    }

    result.labelled += data.labelled;
    result.alreadyLabelled += data.already_labelled || 0;
    result.skipped += (data.not_found || 0) + (data.failed || 0);
    mergePropertyLabels(result.propertyLabels, data.property_labels_arr);
  }

  return result;
}

export function bulkLabelSummary(labelName, { labelled, alreadyLabelled, skipped }) {
  const properties = (n) =>
    `${n.toLocaleString("en-US")} ${n === 1 ? "property" : "properties"}`;
  const parts = [];

  if (labelled > 0) parts.push(`"${labelName}" added to ${properties(labelled)}.`);
  if (alreadyLabelled > 0) parts.push(`${properties(alreadyLabelled)} already had it.`);
  if (skipped > 0) parts.push(`${properties(skipped)} couldn't be labelled.`);

  return parts.join(" ");
}
